import { useState, useEffect } from 'react';
import axios from 'axios';
import { usePublicPageData } from '../context/PublicPageData';
import { useI18n } from '../context/I18nContext';
import { isPublicSpvOfferEnabled } from '../config/featureFlags';

const API = process.env.REACT_APP_BACKEND_URL;

// Simple in-memory cache so multiple components on the same page don't re-fetch.
// Keyed by `${pageId}::${locale}` so FR and EN are cached independently.
const cache = {};

// LOT 537 v15 (Daniel 03/06/2026) — Listeners de cache pour permettre à
// `useSiteContent` / `usePortalContent` de re-render quand l'admin sauvegarde
// inline une page CMS depuis la page elle-même. Pattern pub/sub simple.
const listeners = new Map(); // cacheKey -> Set<setContent>

function cacheKey(prefix, pageId, locale) {
  return `${prefix}::${pageId}::${locale || 'fr'}`;
}

function notifyListeners(key) {
  const subs = listeners.get(key);
  if (!subs) return;
  const fresh = cache[key];
  subs.forEach((setter) => {
    try { setter(fresh); } catch { /* setter unmounted */ }
  });
}

/**
 * Invalide le cache local et re-fetch le contenu d'une page CMS portal/site.
 * Utilisé après un save inline admin pour propager la modification à toutes
 * les instances du hook `usePortalContent` montées sur la page (et déclencher
 * un re-render automatique du composant qui affiche le titre / lede).
 *
 * @param {'public'|'portal'|'legal'|'block'} prefix  type d'endpoint CMS
 * @param {string} pageId
 * @param {string} locale  'fr' ou 'en'
 */
export async function invalidateCmsCache(prefix, pageId, locale = 'fr') {
  const key = cacheKey(prefix, pageId, locale);
  delete cache[key];
  // Re-fetch immédiat pour les listeners actifs (composants montés).
  const endpoint = prefix === 'portal'
    ? `${API}/api/site-content/portal/pages/${pageId}`
    : prefix === 'legal'
      ? `${API}/api/site-content/public/legal/${pageId}`
      : prefix === 'block'
        ? `${API}/api/site-content/public/blocks/${pageId}`
        : `${API}/api/site-content/public/pages/${pageId}`;
  try {
    const res = await axios.get(endpoint, {
      params: { locale },
      withCredentials: prefix === 'portal',
    });
    cache[key] = res.data;
    notifyListeners(key);
    return res.data;
  } catch {
    notifyListeners(key);
    return null;
  }
}

/**
 * Fetch CMS content for a public page in the current locale.
 * Returns { content, loading } where content is the page doc or null.
 * If the API fails, content stays null and the calling component should
 * render its structural shell gracefully.
 */
export function useSiteContent(pageId) {
  const { lang } = useI18n();
  const locale = lang === 'en' ? 'en' : 'fr';
  const key = cacheKey('public', pageId, locale);
  const publicData = usePublicPageData();
  const initial = publicData?.locale === locale ? publicData.cms?.[pageId] : null;
  const [content, setContent] = useState(initial || cache[key] || null);
  const [loading, setLoading] = useState(!initial && !cache[key]);

  useEffect(() => {
    const k = cacheKey('public', pageId, locale);
    if (cache[k]) {
      setContent(cache[k]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/site-content/public/pages/${pageId}`, { params: { locale } })
      .then(res => {
        if (!cancelled) {
          cache[k] = res.data;
          setContent(res.data);
        }
      })
      .catch(() => { /* silent — fallback to null */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pageId, locale]);

  return { content, loading };
}

/**
 * Fetch CMS content for an authenticated portal page in the current locale.
 * Uses the portal endpoint which only returns published content.
 */
export function usePortalContent(pageId) {
  const { lang } = useI18n();
  const locale = lang === 'en' ? 'en' : 'fr';
  const key = cacheKey('portal', pageId, locale);

  const [content, setContent] = useState(cache[key] || null);
  const [loading, setLoading] = useState(!cache[key]);

  // LOT 537 v15 — s'enregistre comme listener pour recevoir les updates
  // de cache push après save inline admin (invalidateCmsCache).
  useEffect(() => {
    const k = cacheKey('portal', pageId, locale);
    let subs = listeners.get(k);
    if (!subs) {
      subs = new Set();
      listeners.set(k, subs);
    }
    subs.add(setContent);
    return () => {
      subs.delete(setContent);
      if (subs.size === 0) listeners.delete(k);
    };
  }, [pageId, locale]);

  useEffect(() => {
    const k = cacheKey('portal', pageId, locale);
    if (cache[k]) {
      setContent(cache[k]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/site-content/portal/pages/${pageId}`, {
      params: { locale },
      withCredentials: true,
    })
      .then(res => {
        if (!cancelled) {
          cache[k] = res.data;
          setContent(res.data);
        }
      })
      .catch(() => { /* silent — fallback to null */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pageId, locale]);

  return { content, loading };
}

/**
 * Helper: get a CMS value or fall back to a default.
 * Usage: cmsVal(content, 'sections.header.title', t('default.title'))
 *
 * Normalisation du branding (display-time only, 0 écriture DB) :
 * - "WM Global Real Assets" / "WM GRA" / "WMGRA" / "Wealth Management"
 *   → remplacés par "Wealth Management - Global Real Assets"
 *   uniquement à l'affichage. La DB n'est jamais modifiée.
 *
 * Filtre de placeholders / contenu de test :
 * - Les valeurs "Test CMS", "test", "placeholder", "lorem ipsum",
 *   "--", etc. sont rejetées → fallback sur la valeur par défaut (i18n).
 *   Cela évite d'exposer des contenus de test restés en DB sans avoir
 *   à les modifier. 0 écriture DB — filtrage display-time uniquement.
 */
const BRAND_OFFICIAL = 'Wealth Management - Global Real Assets';
const PLACEHOLDER_RE = /(?:^\s*(?:test|placeholder|lorem ipsum|--+|\.\.\.|todo)\s*$|\btest\s*cms\b|\bplaceholder\b|\blorem ipsum\b|^\s*test\s+\S{1,40}\s*$|^\s*aide\s+test\s*$|^\s*\S{1,20}\s+test\s*$)/i;

/**
 * LOT 403-C — Garde anti-leak public SPV / sponsor / porteur de projet.
 *
 * Lorsque `REACT_APP_PUBLIC_SPV_OFFER_ENABLED=false` (défaut), aucune valeur
 * CMS ou fallback backend ne doit pouvoir réintroduire publiquement les
 * mentions SPV / SCSp / Créer une SPV / porteur de projet / etc.
 *
 * Stratégie : si la valeur reçue (CMS ou fallback Python) matche un motif
 * interdit, on retourne `null` afin que `cmsVal()` / `safeCmsText()` retombe
 * sur la valeur par défaut frontend (qui est, elle, déjà conditionnée sur le
 * flag et propose un wording neutre).
 *
 * 0 écriture DB · 0 modification du contenu authentifié.
 * S'applique uniquement à l'affichage côté navigateur (display-time guard).
 */
const PUBLIC_SPV_FORBIDDEN_RE = [
  /\bSCSp\b/,
  /\bSPV\b/,
  /\bSAS\s*,\s*SPV\b/i,
  /\bSPV\s*,\s*SAS\b/i,
  /\bcréer\s+une?\s+SPV\b/i,
  /\bcreate\s+(?:an?\s+|your\s+)?SPV\b/i,
  /\boffre\s+SPV\b/i,
  /\btarifs?\s+SPV\b/i,
  /\badministration\s+(?:de\s+)?SPV\b/i,
  /\bSPV\s+administration\b/i,
  /\bporteurs?\s+de\s+projets?\b/i,
  /\bproject\s+(?:owners?|sponsors?)\b/i,
  /\bsubmit\s+(?:your\s+)?projects?\b/i,
  /\bdéposer\s+un\s+projet\b/i,
  /\bsoumettre\s+(?:un\s+)?projets?\b/i,
  /\bdevenez?\s+sponsor\b/i,
  /\bbecome\s+(?:a\s+)?sponsor\b/i,
  /\braise\s+capital\b/i,
];

function containsForbiddenSpvText(s) {
  if (typeof s !== 'string' || !s) return false;
  for (const re of PUBLIC_SPV_FORBIDDEN_RE) {
    if (re.test(s)) return true;
  }
  return false;
}

/**
 * Returns `null` when the input contains a forbidden public SPV/sponsor
 * mention AND the flag is disabled. Otherwise returns the input untouched.
 * The caller (`cmsVal` / `safeCmsText`) interprets `null` as "fall back to
 * the frontend default value".
 */
function sanitizePublicSpv(value) {
  if (typeof value !== 'string' || !value) return value;
  if (isPublicSpvOfferEnabled()) return value;
  return containsForbiddenSpvText(value) ? null : value;
}

/**
 * Strip any value that is obviously test / placeholder content.
 * Returns `null` when the string is empty or matches a test pattern,
 * otherwise returns the trimmed string.
 */
export function cleanCms(value) {
  if (typeof value !== 'string') return value ?? null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (PLACEHOLDER_RE.test(trimmed)) return null;
  // LOT 403-C — drop any CMS / fallback value containing forbidden public
  // SPV / sponsor / porteur-de-projet mentions when the flag is disabled.
  if (sanitizePublicSpv(trimmed) === null) return null;
  return trimmed;
}

/**
 * Safe display of a CMS text: falls back to the provided default when
 * the CMS value is empty or contains placeholder/test content; also runs
 * the brand normalizer so forbidden brand variants never leak through.
 * Works transparently whether `raw` is a plain string or already sanitized.
 */
export function safeCmsText(raw, fallback) {
  const cleaned = cleanCms(raw);
  if (cleaned === null || cleaned === undefined) {
    return typeof fallback === 'string' ? normalizeBrand(fallback) : fallback;
  }
  return normalizeBrand(cleaned);
}

function normalizeBrand(s) {
  if (typeof s !== 'string') return s;
  // Ne pas normaliser les URLs / chemins / fichiers (ex: /images/logo-wmgra.png)
  const looksLikeUrl = /^(https?:\/\/|\/|\.\.?\/|file:)/i.test(s)
    || /\.(png|jpe?g|gif|svg|webp|bmp|ico|mp4|webm|pdf|zip|json|css|js|html?)(\?|$)/i.test(s);
  if (looksLikeUrl) return s;

  return s
    .replace(/\bWM\s+Global\s+Real\s+Assets\b/gi, BRAND_OFFICIAL)
    .replace(/\bWM\s+GRA\b/gi, BRAND_OFFICIAL)
    .replace(/\bWMGRA\b/gi, BRAND_OFFICIAL)
    .replace(/\bCapital\s+Circle\b/gi, BRAND_OFFICIAL);
}

/* ─── PHASE 9 — ARBITRAGE PO LOT 403-C (option C) ────────────────────────
   Le remplacement automatique « investissement → projet » /
   « co-investissement → co-participation » est SUPPRIMÉ du chemin
   d'affichage PUBLIC : il dégradait la qualité rédactionnelle (décision PO).
   Il est remplacé par une politique éditoriale contextuelle appliquée à la
   sauvegarde/publication CMS (garde-fou anti-sollicitation, backend
   core/editorial_policy.py) — le système signale, ne réécrit jamais.

   Le PORTAIL INVESTISSEUR (sanctuaire v22) conserve à l'identique son
   vocabulaire display-time historique : `legacyPortalVocab` n'est appliqué
   que lorsque le doc CMS vient d'une page `category === 'portal'`.
   Le garde anti-SPV lié au feature flag reste intact (cleanCms). ──────── */
function legacyPortalVocab(s) {
  if (typeof s !== 'string') return s;
  const looksLikeUrl = /^(https?:\/\/|\/|\.\.?\/|file:)/i.test(s)
    || /\.(png|jpe?g|gif|svg|webp|bmp|ico|mp4|webm|pdf|zip|json|css|js|html?)(\?|$)/i.test(s);
  if (looksLikeUrl) return s;

  let out = s
    .replace(/\bde\s+qualité\s+institutionnelle\b/gi, 'sur mesure')
    .replace(/\bqualité\s+institutionnelle\b/gi, 'sur mesure')
    .replace(/\binstitutional[-\s]quality\b/gi, 'tailored')
    .replace(/\binstitutional[-\s]grade\b/gi, 'tailored');

  out = out
    .replace(/\bL['\u2019]investissement en actifs réels\b/g, 'Le financement de projets en actifs réels')
    .replace(/\bl['\u2019]investissement en actifs réels\b/g, 'le financement de projets en actifs réels');

  out = out
    .replace(/\bco-investissements\b/gi, 'co-participations')
    .replace(/\bco-investissement\b/gi, 'co-participation')
    .replace(/\bInvestissements\b/g, 'Projets')
    .replace(/\binvestissements\b/g, 'projets')
    .replace(/\bINVESTISSEMENT\b/g, 'PROJET')
    .replace(/\bInvestissement\b/g, 'Projet')
    .replace(/\binvestissement\b/g, 'projet')
    .replace(/\bco-investments\b/gi, 'co-participations')
    .replace(/\bco-investment\b/gi, 'co-participation')
    .replace(/\bInvestments\b/g, 'Projects')
    .replace(/\binvestments\b/g, 'projects')
    .replace(/\bINVESTMENT\b/g, 'PROJECT')
    .replace(/\bInvestment\b/g, 'Project')
    .replace(/\binvestment\b/g, 'project');

  out = out
    .replace(/\bd'projets\b/gi, 'de projets')
    .replace(/\bd\u2019projets\b/gi, 'de projets')
    .replace(/\bd'projet\b/gi, 'de projet')
    .replace(/\bd\u2019projet\b/gi, 'de projet')
    .replace(/\bà l'projets\b/gi, 'aux projets')
    .replace(/\bà l\u2019projets\b/gi, 'aux projets')
    .replace(/\bà l'projet\b/gi, 'au projet')
    .replace(/\bà l\u2019projet\b/gi, 'au projet')
    .replace(/\bde l'projets\b/gi, 'des projets')
    .replace(/\bde l\u2019projets\b/gi, 'des projets')
    .replace(/\bde l'projet\b/gi, 'du projet')
    .replace(/\bde l\u2019projet\b/gi, 'du projet')
    .replace(/\bL'projets\b/g, 'Les projets')
    .replace(/\bL\u2019projets\b/g, 'Les projets')
    .replace(/\bl'projets\b/g, 'les projets')
    .replace(/\bl\u2019projets\b/g, 'les projets')
    .replace(/\bL'projet\b/g, 'Le projet')
    .replace(/\bL\u2019projet\b/g, 'Le projet')
    .replace(/\bl'projet\b/g, 'le projet')
    .replace(/\bl\u2019projet\b/g, 'le projet')
    .replace(/\bà aux projets en\b/gi, 'aux projets en')
    .replace(/\bà au projet en\b/gi, 'au projet en');

  return out;
}

export function cmsVal(content, path, fallback) {
  const isPortal = content?.category === 'portal';
  const post = (s) => {
    if (typeof s !== 'string') return s;
    const branded = normalizeBrand(s);
    return isPortal ? legacyPortalVocab(branded) : branded;
  };
  const fb = post(fallback);
  if (!content) return fb;
  const parts = path.split('.');
  let val = content;
  for (const p of parts) {
    if (val == null) return fb;
    val = val[p];
  }
  if (typeof val === 'string') {
    const cleaned = cleanCms(val);
    if (cleaned === null) return fb;
    return post(cleaned);
  }
  if (val != null) return val;
  return fb;
}


/**
 * Fetch CMS content for a legal page in the current locale.
 */
export function useLegalContent(pageId) {
  const { lang } = useI18n();
  const locale = lang === 'en' ? 'en' : 'fr';
  const key = cacheKey('legal', pageId, locale);

  const [content, setContent] = useState(cache[key] || null);
  const [loading, setLoading] = useState(!cache[key]);

  useEffect(() => {
    const k = cacheKey('legal', pageId, locale);
    if (cache[k]) {
      setContent(cache[k]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/site-content/public/legal/${pageId}`, { params: { locale } })
      .then(res => {
        if (!cancelled) {
          cache[k] = res.data;
          setContent(res.data);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pageId, locale]);

  return { content, loading };
}

/**
 * Fetch CMS content for a reusable block in the current locale.
 */
export function useBlockContent(blockId) {
  const { lang } = useI18n();
  const locale = lang === 'en' ? 'en' : 'fr';
  const key = cacheKey('block', blockId, locale);

  const [content, setContent] = useState(cache[key] || null);
  const [loading, setLoading] = useState(!cache[key]);

  useEffect(() => {
    const k = cacheKey('block', blockId, locale);
    if (cache[k]) {
      setContent(cache[k]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/site-content/public/blocks/${blockId}`, { params: { locale } })
      .then(res => {
        if (!cancelled) {
          cache[k] = res.data;
          setContent(res.data);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [blockId, locale]);

  return { content, loading };
}
